import React, { useState } from 'react';
import { useInView } from '../../../hooks/useInView';

const STEPS = [
  {
    num: '01',
    title: 'Enter a business name and address',
    body: 'The physical address locks in the exact location, no ambiguity about which "Main Street Pizza" you mean.',
    detail: 'Works for any local business: restaurants, salons, gyms, dental offices, auto shops, and 3,000+ more categories.',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
      </svg>
    ),
  },
  {
    num: '02',
    title: 'Enter up to 5 leadership titles',
    body: 'Owner, GM, Operations Manager. We search for those specific roles at that specific address.',
    detail: 'The more titles you add, the more contacts you uncover. Each matched role costs nothing extra, still 1 credit.',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" />
      </svg>
    ),
  },
  {
    num: '03',
    title: 'Get every contact we find',
    body: 'Live results come back with full name and job title. Export to CSV in one click. All for 1 credit per company.',
    detail: 'No result = no charge. Credits are only consumed when we successfully return at least one contact.',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
      </svg>
    ),
  },
];

export default function Methodology() {
  const [active, setActive] = useState<number | null>(null);
  const { ref, inView } = useInView(0.1);

  return (
    <section id="how-it-works" ref={ref as React.RefObject<HTMLElement>} className="bg-white py-24 sm:py-36 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">

        {/* header */}
        <div
          className="mb-20"
          style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(24px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
        >
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-brand-700 mb-4">How it works</p>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-[-0.04em] text-slate-900 leading-[0.95]">
              Three inputs.<br />Real decision makers.
            </h2>
            <p className="text-[15px] text-slate-400 max-w-[260px] sm:text-right leading-relaxed flex-shrink-0">
              No setup, no learning curve. Enter the details and get contacts back.
            </p>
          </div>
        </div>

        {/* steps */}
        <div className="space-y-5">
          {STEPS.map((s, i) => {
            const isActive = active === i;
            return (
              <div
                key={s.num}
                className={`rounded-3xl border-2 cursor-pointer transition-all duration-300 overflow-hidden ${
                  isActive
                    ? 'border-brand-500 bg-[#060A12]'
                    : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white'
                }`}
                style={{
                  opacity: inView ? 1 : 0,
                  transform: inView ? 'translateY(0)' : 'translateY(32px)',
                  transition: `opacity 0.5s ease ${i * 120}ms, transform 0.5s ease ${i * 120}ms, border-color 0.3s ease, background-color 0.3s ease`,
                }}
                onClick={() => setActive(isActive ? null : i)}
              >
                <div className="flex items-center gap-6 p-6 sm:p-8">
                  {/* yellow number box */}
                  <div className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center font-black text-[1.8rem] sm:text-[2.2rem] transition-all duration-300 ${
                    isActive ? 'bg-brand-500 text-slate-900' : 'bg-white border-2 border-slate-200 text-slate-300'
                  }`}>
                    {s.num}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className={`text-[11px] font-black uppercase tracking-widest mb-1.5 transition-colors duration-200 ${
                      isActive ? 'text-brand-500' : 'text-slate-400'
                    }`}>Step {s.num}</div>
                    <h3 className={`text-[18px] sm:text-[22px] font-black leading-snug transition-colors duration-200 ${
                      isActive ? 'text-white' : 'text-slate-900'
                    }`}>{s.title}</h3>
                    <p className={`text-[14px] leading-relaxed mt-1.5 transition-colors duration-200 ${
                      isActive ? 'text-slate-400' : 'text-slate-500'
                    }`}>{s.body}</p>
                  </div>

                  {/* icon */}
                  <div className={`hidden sm:flex w-14 h-14 rounded-xl items-center justify-center flex-shrink-0 transition-all duration-300 ${
                    isActive ? 'bg-brand-500/15 text-brand-400' : 'bg-white text-slate-300 border border-slate-200'
                  }`}>
                    {s.icon}
                  </div>

                  {/* chevron */}
                  <svg
                    className={`w-5 h-5 flex-shrink-0 transition-all duration-300 ${isActive ? 'text-brand-500 rotate-180' : 'text-slate-300 rotate-0'}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>

                {/* expanded detail */}
                <div style={{
                  maxHeight: isActive ? '120px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                }}>
                  <div className="px-6 sm:px-8 pb-6 flex items-start gap-3">
                    <div className="w-1 h-full bg-brand-500 rounded-full flex-shrink-0 self-stretch min-h-[40px]" />
                    <p className="text-[14px] text-slate-400 leading-relaxed">{s.detail}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
